// Thin Tiptap wrapper used by the email broadcast page. Toolbar exposes
// the formatting set the server's sanitiser allows: bold / italic /
// underline / bullet + ordered list / link. Output is HTML; the server
// re-sanitises with `xss` before persisting / sending so any extension we
// later turn on is still bounded.

import { useEffect } from "react";
import { Box, Divider, IconButton, Tooltip } from "@mui/material";
import {
  FormatBold,
  FormatItalic,
  FormatUnderlined,
  FormatListBulleted,
  FormatListNumbered,
  Link as LinkIcon,
  LinkOff,
} from "@mui/icons-material";
import { useEditor, EditorContent, type Editor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import Link from "@tiptap/extension-link";
import { tokens } from "@findgeeks/ui-tokens";

interface Props {
  value: string;
  onChange: (html: string) => void;
  minRows?: number;
  placeholder?: string;
}

function ToolbarButton(props: {
  title: string;
  active?: boolean;
  disabled?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Tooltip title={props.title}>
      <span>
        <IconButton
          size="small"
          disabled={props.disabled}
          onClick={props.onClick}
          sx={{
            color: props.active
              ? tokens.colors.admin.primary
              : tokens.colors.admin.textBody,
            backgroundColor: props.active
              ? "rgba(0,123,255,0.08)"
              : "transparent",
            borderRadius: 0.75,
          }}
        >
          {props.children}
        </IconButton>
      </span>
    </Tooltip>
  );
}

function Toolbar({ editor }: { editor: Editor }) {
  const promptForLink = () => {
    const previous = editor.getAttributes("link").href ?? "";
    const url = window.prompt("URL:", previous);
    if (url === null) return; // cancelled
    if (url === "") {
      editor.chain().focus().extendMarkRange("link").unsetLink().run();
      return;
    }
    editor
      .chain()
      .focus()
      .extendMarkRange("link")
      .setLink({ href: url, target: "_blank", rel: "noopener noreferrer" })
      .run();
  };

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 0.25,
        px: 0.5,
        py: 0.5,
        borderBottom: `1px solid ${tokens.colors.admin.border}`,
      }}
    >
      <ToolbarButton
        title="Bold"
        active={editor.isActive("bold")}
        onClick={() => editor.chain().focus().toggleBold().run()}
      >
        <FormatBold fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Italic"
        active={editor.isActive("italic")}
        onClick={() => editor.chain().focus().toggleItalic().run()}
      >
        <FormatItalic fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Underline"
        active={editor.isActive("underline")}
        onClick={() => editor.chain().focus().toggleUnderline().run()}
      >
        <FormatUnderlined fontSize="small" />
      </ToolbarButton>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
      <ToolbarButton
        title="Bulleted list"
        active={editor.isActive("bulletList")}
        onClick={() => editor.chain().focus().toggleBulletList().run()}
      >
        <FormatListBulleted fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Numbered list"
        active={editor.isActive("orderedList")}
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
      >
        <FormatListNumbered fontSize="small" />
      </ToolbarButton>
      <Divider orientation="vertical" flexItem sx={{ mx: 0.5, my: 0.75 }} />
      <ToolbarButton
        title="Insert / edit link"
        active={editor.isActive("link")}
        onClick={promptForLink}
      >
        <LinkIcon fontSize="small" />
      </ToolbarButton>
      <ToolbarButton
        title="Remove link"
        disabled={!editor.isActive("link")}
        onClick={() =>
          editor.chain().focus().extendMarkRange("link").unsetLink().run()
        }
      >
        <LinkOff fontSize="small" />
      </ToolbarButton>
    </Box>
  );
}

export function WysiwygEditor({
  value,
  onChange,
  minRows = 8,
  placeholder,
}: Props) {
  const editor = useEditor({
    // Tiptap StarterKit ships Bold/Italic/Strike/Code, Heading, Paragraph,
    // BulletList/OrderedList/ListItem, Blockquote, HardBreak, etc.
    // Underline + Link are separate packages — included above.
    extensions: [
      StarterKit,
      Underline,
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { rel: "noopener noreferrer", target: "_blank" },
      }),
    ],
    content: value,
    onUpdate: ({ editor }) => onChange(editor.getHTML()),
    editorProps: {
      attributes: {
        class: "tt-content",
        style: `min-height: ${minRows * 24}px; padding: 12px 14px; outline: none;`,
        ...(placeholder ? { "data-placeholder": placeholder } : {}),
      },
    },
  });

  // External value resets (e.g. Discard click) need a manual setContent —
  // Tiptap doesn't sync `content` prop after first mount.
  useEffect(() => {
    if (!editor) return;
    if (value !== editor.getHTML()) {
      editor.commands.setContent(value || "<p></p>", { emitUpdate: false });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  if (!editor) return null;

  return (
    <Box
      sx={{
        border: `1px solid ${tokens.colors.admin.border}`,
        borderRadius: 1,
        backgroundColor: "#fff",
        // Tiptap's default rendering — keep simple and inherit MUI font.
        "& .tt-content": {
          fontFamily: "inherit",
          fontSize: tokens.font.sizes.body,
          lineHeight: 1.55,
        },
        "& .tt-content p": { margin: "0 0 0.6em" },
        "& .tt-content ul, & .tt-content ol": { paddingLeft: "1.4em" },
        "& .tt-content a": { color: tokens.colors.admin.primary },
        // Empty-content placeholder via data attribute.
        "& .tt-content p.is-editor-empty:first-of-type::before": {
          color: tokens.colors.admin.textMuted,
          content: "attr(data-placeholder)",
          float: "left",
          height: 0,
          pointerEvents: "none",
        },
      }}
    >
      <Toolbar editor={editor} />
      <EditorContent editor={editor} />
    </Box>
  );
}
